<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="true"
          title="Bill Collection"
          @onClickCloseButton="navigateToListPage"
      />
    </div>
    <div class="my-2 px-2">
        <div class="row">
          <div class="col-12 col-sm-6 col-md-4 col-lg-3">
            <label
                for="colFormLabel"
                class="col-form-label"
            >
              Client
            </label>
            <AsyncSelect
                placeholder="Select Client"
                v-model="clientContactProfile"
                :api-service="fetchContactProfiles"
                :additional-query="{ type: 'client' }"
                :format-label="formatPatientLabel"
                :reduce="referrer => referrer.id"
            />
          </div>
          <div class="col-sm-2 col-md-1 d-flex align-items-end mt-2 action-btn-setter">
            <button
                style="min-width: 64px;"
                @click="onClickToGo"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            > GO
            </button>
          </div>
        </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-end px-2">
          <button
              @click="onClickToPrint"
              class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
          >
            Draft Statement Print
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 px-2 mt-1 mb-4">
      <div class="card p-1" style="min-height: 150px">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th class="text-center">Invoice Date</th>
              <th class="text-center">Invoice No</th>
              <th class="text-center">Patient Name (ID)</th>
              <th class="text-center">Service Details</th>
              <th class="text-center">Total Invoice</th>
              <th class="text-center">Total Discount</th>
              <th class="text-center">Net Bill</th>
              <th class="text-center">Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in tableItems" :key="i">
              <td>{{ item.date }}</td>
              <td>{{ item.bill_number }}</td>
              <td>{{ item.contact_profile_name }} ({{ item.contact_profile_serial_no }})</td>
              <td>{{ item.product_details }}</td>
              <td class="text-right">{{ item.subtotal_amount }}</td>
              <td class="text-right">{{ item.total_discount }}</td>
              <td class="text-right">{{ item.total_amount }}</td>
              <td class="text-center">
                <input type="checkbox" @change="toggleSelection(item)" />
              </td>
            </tr>
            <tr v-if="tableItems.length > 0">
              <td colspan="4">Total</td>
              <td class="text-right">{{ totalData.total_invoice }}</td>
              <td class="text-right">{{ totalData.total_discount }}</td>
              <td class="text-right">{{ totalData.net_bill }}</td>
              <td class="text-right"></td>
            </tr>
            <tr v-if="selectedItems.length > 0">
              <td colspan="4">Processed Amount</td>
              <td class="text-right">{{ selectedTotals.total_invoice }}</td>
              <td class="text-right">{{ selectedTotals.total_discount }}</td>
              <td class="text-right">{{ selectedTotals.net_bill }}</td>
              <td class="text-right"></td>
            </tr>
            </tbody>
          </table>
          <p v-if="tableItems.length <= 0" class="text-center mt-5">No data</p>
        </div>
      </div>
    </div>
    </div>

    <div class="my-2 px-2">
    <div class="row mt-1" v-if="tableItems.length > 0">
      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <input
            v-model="date"
            name="datetime"
            type="date"
            class="form-control"
        />
      </div>
      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <button :disabled="saveButtonLoader" @click="saveData()"
                class="btn btn-primary">
          Save
        </button>
      </div>
    </div>
    </div>

    <Loader v-if="loading"/>

  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, reactive, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import { computed } from 'vue';
import handleInventory from "@/services/modules/inventory";
import handleHospital from "@/services/modules/hospital";
import handleContact from '@/services/modules/contact'
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import useDate from "@/services/utils/day";
import incentivePrinter from '@/services/utils/pdf/incentivePaymentDraft';
import handleCompany from "@/services/modules/company";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const saveButtonLoader = ref(false)
const clientContactProfile = ref(null)
const date = ref(null)
const tableItems = ref([]);
const totalData = ref({});
const incentivePrintData = ref({});
const status = ref([]);
const params = ref({
  offset: 30,
});
let companyInfo = reactive({})

const token = computed(() => localStorage.getItem('token'));

const { fetchUnpaidBillCollection, storeIncentivePayment } = handleHospital()
const {fetchContactProfiles} = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchHome } = handleInventory()
const dateValue = useDate();
const { exportToPDF } = incentivePrinter();
const { fetchCompanyInfo } = handleCompany();
const selectedItems = ref([]);

const onClickToGo = () => {
  getUnpaidBillCollectionList();
}

function navigateToListPage() {
  router.push({name: `bill-collection-list`, params: route.params, query: route.query});
}

const selectedTotals = computed(() => {
  return selectedItems.value.reduce((totals, item) => {
    totals.total_invoice += item.subtotal_amount;
    totals.total_discount += item.total_discount;
    totals.net_bill += item.total_amount;
    return totals;
  }, {
    total_invoice: 0,
    total_discount: 0,
    net_bill: 0,
  });
});

const toggleSelection = (item) => {
  const index = selectedItems.value.findIndex((selected) => selected.id === item.id);
  if (index === -1) {
    selectedItems.value.push(item);
  } else {
    selectedItems.value.splice(index, 1);
  }
};

const saveData = () => {
  if (selectedItems.value.length === 0) {
    showError("Please select at least one record.");
    return;
  }

  let incentivePaymentData = {
    company_id: route.params.companyId,
    contact_profile_id: clientContactProfile.value,
    date: date.value,
    status: "approved",
    type: "bill_collection",
    item_details: selectedItems.value.map((item) => ({
      sale_master_id: item.id,
      total_invoice: item.subtotal_amount,
      total_discount: item.total_discount,
      net_bill: item.total_amount,
    })),
  };

    storeIncentivePayment(incentivePaymentData, getQuery())
        .then(res => {
          if (!res.status) {
            showError(res.message)
            return
          }
          showSuccess(res.message)
          navigateToListPage()
        })
        .catch(err => {
          console.log(err)
        }).finally(() => {
          saveButtonLoader.value = false
     })
}

const getUnpaidBillCollectionList = async () => {
  const query = getQuery();
  loading.value = true
  tableItems.value = []

  await fetchUnpaidBillCollection(query).then(res => {
    if (!res.status){
      showError(res.message)
      return
    }

    tableItems.value = Array.isArray(res.data.data)
        ? res.data.data
        : Object.values(res.data.data);

    totalData.value = res.data.total;
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const onClickToPrint = async () => {
  let query = getQuery()

  await fetchUnpaidBillCollection(query).then((res) => {
    if (!res.status) {
      showError(res.message)
      return;
    }
    incentivePrintData.value = res
  }).then(() => {
    exportToPDF(companyInfo,incentivePrintData.value, false, true)
  }).catch((err) => {
    console.log("Something went wrong");
  });
}

const setCompanyInfo = async () => {
  await fetchCompanyInfo(route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  if (clientContactProfile.value) query += '&contact_profile_client_id=' + clientContactProfile.value;
  return query;
}

onMounted(() => {
   date.value = dateValue.currentDate();
   setCompanyInfo()
})

</script>
